import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { refreshData, fetchMasterData } from '../../store/slices/masterDataSlice';
import { CourseSelect } from '../../components/MasterDataSelect';
import { API_BASE_URL } from '../../utils/apiConfig';
import { showSuccess, showError } from '../../components/CustomAlert';

const Container = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
`;

const FormContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
  
  &::after {
    content: ${props => props.required ? '"*"' : '""'};
    color: #dc3545;
    margin-left: 4px;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const GenderGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
`;

const RadioLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  
  &:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const CountryCode = styled.div`
  background: #f8f9fa;
  padding: 10px 12px;
  border-right: 1px solid #ddd;
  font-size: 14px;
`;

const PhoneInput = styled.input`
  border: none;
  padding: 10px 12px;
  font-size: 14px;
  flex: 1;
  
  &:focus {
    outline: none;
  }
`;

const FileInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  color: #6c757d;
  flex: 1;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const UploadButton = styled.label`
  background: #f3f3f3;
  color: #212529;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: #ffca2c;
  }
`;

const AddressGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddressRow = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const AddressInput = styled(Input)`
  flex: 1;
`;

const AddressLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const StatusSelect = styled(Select)`
  background: white;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  grid-column: 1 / -1;
`;

const SubmitButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #0056b3;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

const ResetButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #545b62;
  }
`;

const SignaturePadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SignatureCanvas = styled.canvas`
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: crosshair;
  background: #f8f9fa;
  width: 300px;
  height: 150px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const ClearSignatureButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  
  &:hover {
    background: #545b62;
  }
`;

const AddEmployee = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeId: '',
    employeeType: '',
    salutation: '',
    contactNumber: '',
    email: '',
    qualification: '',
    dateOfBirth: '',
    course: '',
    gender: '',
    addressLine1: '',
    maritalStatus: '',
    addressLine2: '',
    photo: null,
    cityDistrict: '',
    stateProvince: '',
    postalCode: '',
    designation: '',
    country: '',
    status: 'Active',
    signature: ''
  });

  const [phoneError, setPhoneError] = useState('');
  const signatureCanvasRef = useRef(null);
  const [signatureContext, setSignatureContext] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Initialize signature canvas
  const initSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    canvas.style.width = '300px';
    canvas.style.height = '150px';
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
      ctx.strokeStyle = '#1a202c';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setSignatureContext(ctx);
    }
  }, []);

  useEffect(() => {
    initSignatureCanvas();
    window.addEventListener('resize', initSignatureCanvas);
    return () => window.removeEventListener('resize', initSignatureCanvas);
  }, [initSignatureCanvas]);

  const startSignatureDrawing = (e) => {
    if (!signatureContext) return;
    setIsDrawing(true);
    signatureContext.beginPath();
    signatureContext.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  };

  const drawSignature = (e) => {
    if (!isDrawing || !signatureContext) return;
    signatureContext.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    signatureContext.stroke();
  };

  const stopSignatureDrawing = () => {
    if (!signatureContext) return;
    setIsDrawing(false);
    const signatureDataUrl = signatureCanvasRef.current.toDataURL();
    setFormData(prev => ({ ...prev, signature: signatureDataUrl }));
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!signatureContext || !canvas) return;
    signatureContext.clearRect(0, 0, canvas.width, canvas.height);
    signatureContext.fillStyle = '#ffffff';
    signatureContext.fillRect(0, 0, canvas.width / (Math.max(window.devicePixelRatio || 1, 1)), canvas.height / (Math.max(window.devicePixelRatio || 1, 1)));
    setFormData(prev => ({ ...prev, signature: '' }));
  };

  const salutations = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
  const maritalStatuses = ['Single', 'Married', 'Divorced', 'Widowed'];
  const courses = ['EKG & PHL', 'CNA', 'CPR/AED/BLS', 'Phlebotomy', 'Medical Assistant'];
  const designations = ['Instructor', 'Senior Instructor', 'Lead Instructor', 'Department Head'];
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia'];
  const employeeTypes = ['Instructor', 'Staff Member'];

  // Phone number validation functions
  const validatePhoneNumber = (phone) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Check if it's exactly 10 digits (US phone number)
    if (digits.length === 0) {
      return { isValid: true, error: '' }; // Allow empty
    }
    
    if (digits.length !== 10) {
      return { isValid: false, error: 'Phone number must be exactly 10 digits' };
    }
    
    return { isValid: true, error: '' };
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Format as XXX-XXX-XXXX
    if (limitedDigits.length >= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6, 10)}`;
    } else if (limitedDigits.length >= 3) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else if (limitedDigits.length > 0) {
      return limitedDigits;
    }
    
    return '';
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    const validation = validatePhoneNumber(value);
    
    setFormData(prev => ({ ...prev, contactNumber: formatted }));
    setPhoneError(validation.error);
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const requiredFields = [
      'employeeName',
      'employeeType',
      'contactNumber',
      'qualification',
      'email',
      'dateOfBirth',
      'gender'
    ];

    // Check if all required fields have values
    const hasAllRequiredFields = requiredFields.every(field => {
      const value = formData[field];
      return value && value.toString().trim() !== '';
    });

    // Check if phone number is valid (if provided)
    const isPhoneValid = !formData.contactNumber || validatePhoneNumber(formData.contactNumber).isValid;

    return hasAllRequiredFields && isPhoneValid;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      photo: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number before submission
    if (formData.contactNumber) {
      const validation = validatePhoneNumber(formData.contactNumber);
      if (!validation.isValid) {
        setPhoneError(validation.error);
        return;
      }
    }
    
    try {
      const data = new FormData();
      data.append('employee_id', formData.employeeId);
      data.append('name', formData.employeeName);
      data.append('employee_type', formData.employeeType);
      data.append('salutation', formData.salutation);
      data.append('contact_number', formData.contactNumber);
      data.append('email', formData.email);
      data.append('qualification', formData.qualification);
      data.append('course', formData.course);
      data.append('date_of_birth', formData.dateOfBirth);
      data.append('gender', formData.gender);
      data.append('marital_status', formData.maritalStatus);
      data.append('designation', formData.designation);
      data.append('status', formData.status);
      data.append('address_line1', formData.addressLine1);
      data.append('address_line2', formData.addressLine2);
      data.append('city', formData.cityDistrict);
      data.append('state', formData.stateProvince);
      data.append('postal_code', formData.postalCode);
      data.append('country', formData.country);
      if (formData.photo) data.append('photo', formData.photo);
      if (formData.signature) data.append('signature', formData.signature);

      const endpoint = formData.employeeType === 'Instructor' ? '/instructors/add' : '/staff/add';
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        showSuccess(response.data.message || 'Employee added successfully.');
        // Clear phone error on success
        setPhoneError('');
        // Refresh master data after successful addition
        dispatch(refreshData());
        dispatch(fetchMasterData());
        setFormData({
          employeeName: '', employeeId: '', employeeType: '', salutation: '', contactNumber: '', email: '', qualification: '', dateOfBirth: '', course: '', gender: '', addressLine1: '', maritalStatus: '', addressLine2: '', photo: null, cityDistrict: '', stateProvince: '', course: '', postalCode: '', designation: '', country: '', status: 'Active', signature: ''
        });
      } else {
        showError(response.data.message || 'Failed to add employee.');
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Server error while adding employee.');
    }
  };

  const handleReset = () => {
    setFormData({
      employeeName: '',
      employeeId: '',
      employeeType: '',
      salutation: '',
      contactNumber: '',
      email: '',
      qualification: '',
      dateOfBirth: '',
      course: '',
      gender: '',
      addressLine1: '',
      maritalStatus: '',
      addressLine2: '',
      photo: null,
      cityDistrict: '',
      stateProvince: '',
      postalCode: '',
      designation: '',
      country: '',
      status: 'Active',
      signature: ''
    });
    setPhoneError('');
    clearSignature();
  };

  return (
    <Container>
      <Title>Add Employee</Title>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            {/* Left Column */}
            <FormColumn>
              <FormGroup>
                <Label required>Employee Name</Label>
                <Input
                  type="text"
                  value={formData.employeeName}
                  onChange={(e) => handleInputChange('employeeName', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label required>Employee Type</Label>
                <Select
                  value={formData.employeeType}
                  onChange={(e) => handleInputChange('employeeType', e.target.value)}
                  required
                >
                  <option value="">- Select Employee Type</option>
                  {employeeTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Salutation</Label>
                <Select
                  value={formData.salutation}
                  onChange={(e) => handleInputChange('salutation', e.target.value)}
                >
                  <option value="">- Select</option>
                  {salutations.map((sal, index) => (
                    <option key={index} value={sal}>{sal}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label required>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label required>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label required>Gender</Label>
                <GenderGroup>
                  <RadioGroup>
                    <RadioInput
                      type="radio"
                      id="male"
                      name="gender"
                      value="Male"
                      checked={formData.gender === 'Male'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      required
                    />
                    <RadioLabel htmlFor="male">Male</RadioLabel>
                  </RadioGroup>
                  <RadioGroup>
                    <RadioInput
                      type="radio"
                      id="female"
                      name="gender"
                      value="Female"
                      checked={formData.gender === 'Female'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      required
                    />
                    <RadioLabel htmlFor="female">Female</RadioLabel>
                  </RadioGroup>
                  <RadioGroup>
                    <RadioInput
                      type="radio"
                      id="others"
                      name="gender"
                      value="Others"
                      checked={formData.gender === 'Others'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      required
                    />
                    <RadioLabel htmlFor="others">Others</RadioLabel>
                  </RadioGroup>
                </GenderGroup>
              </FormGroup>

              <FormGroup>
                <Label>Marital Status</Label>
                <Select
                  value={formData.maritalStatus}
                  onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                >
                  <option value="">- Select</option>
                  {maritalStatuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Photo</Label>
                <FileInputContainer>
                  <FileInput
                    type="file"
                    id="photo"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFileChange}
                  />
                  <FileInputLabel htmlFor="photo">
                    {formData.photo ? formData.photo.name : 'Select Image'}
                  </FileInputLabel>
                  <UploadButton htmlFor="photo">📁</UploadButton>
                </FileInputContainer>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  Accepted formats: JPEG, PNG, GIF, WebP, PDF (max 5MB)
                </div>
              </FormGroup>

              {formData.employeeType === 'Instructor' && (
                <>
                  <FormGroup>
                    <Label>Designation</Label>
                    <Select
                      value={formData.designation}
                      onChange={(e) => handleInputChange('designation', e.target.value)}
                    >
                      <option value="">- Select </option>
                      {designations.map((designation, index) => (
                        <option key={index} value={designation}>{designation}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label required>Status</Label>
                <StatusSelect
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  required
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </StatusSelect>
              </FormGroup>
            </FormColumn>

            {/* Right Column */}
            <FormColumn>
              {formData.employeeType === 'Instructor' && (
                <FormGroup>
                  <Label>Instructor ID</Label>
                  <Input
                    type="text"
                    value={formData.employeeId}
                    onChange={(e) => handleInputChange('employeeId', e.target.value)}
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label required>Contact Number</Label>
                <PhoneInputContainer>
                  <CountryCode>🇺🇸 +1</CountryCode>
                  <PhoneInput
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="222-555-7777"
                    maxLength="12"
                    required
                    onKeyPress={(e) => {
                      // Only allow numbers and backspace
                      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      const digitsOnly = pastedText.replace(/\D/g, '');
                      if (digitsOnly.length <= 10) {
                        handlePhoneChange(digitsOnly);
                      }
                    }}
                  />
                </PhoneInputContainer>
                {phoneError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{phoneError}</div>}
              </FormGroup>

              <FormGroup>
                <Label required>Qualification</Label>
                <Input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => handleInputChange('qualification', e.target.value)}
                  required
                />
              </FormGroup>

              {formData.employeeType === 'Instructor' && (
                <FormGroup>
                  <Label>Course</Label>
                  <CourseSelect
                    value={formData.course}
                    onChange={(value) => setFormData(prev => ({ ...prev, course: value }))}
                    placeholder="Select course"
                    isClearable={false}
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>Address</Label>
                <AddressGroup>
                  <div>
                    <AddressInput
                      type="text"
                      value={formData.addressLine1}
                      onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    />
                    <AddressLabel>Address Line 1</AddressLabel>
                  </div>
                  <div>
                    <AddressInput
                      type="text"
                      value={formData.addressLine2}
                      onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    />
                    <AddressLabel>Address Line 2</AddressLabel>
                  </div>
                  <AddressRow>
                    <div style={{ flex: 1 }}>
                      <AddressInput
                        type="text"
                        value={formData.cityDistrict}
                        onChange={(e) => handleInputChange('cityDistrict', e.target.value)}
                      />
                      <AddressLabel>City / District</AddressLabel>
                    </div>
                    <div style={{ flex: 1 }}>
                      {/* <AddressInput
                        type="text"
                        value={formData.stateProvince}
                        onChange={(e) => handleInputChange('stateProvince', e.target.value)}
                      /> */}

                      <Select name="state" placeholder="Select" value={formData.stateProvince || ''} onChange={(e) => handleInputChange('stateProvince', e.target.value)} style={{ marginTop: 8 }}>
                        <option value="">- Select</option>
                        <option value="AL">Alabama</option>
                        <option value="AK">Alaska</option>
                        <option value="AZ">Arizona</option>
                        <option value="AR">Arkansas</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="DE">Delaware</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="HI">Hawaii</option>
                        <option value="ID">Idaho</option>
                        <option value="IL">Illinois</option>
                        <option value="IN">Indiana</option>
                        <option value="IA">Iowa</option>
                        <option value="KS">Kansas</option>
                        <option value="KY">Kentucky</option>
                        <option value="LA">Louisiana</option>
                        <option value="ME">Maine</option>
                        <option value="MD">Maryland</option>
                        <option value="MA">Massachusetts</option>
                        <option value="MI">Michigan</option>
                        <option value="MN">Minnesota</option>
                        <option value="MS">Mississippi</option>
                        <option value="MO">Missouri</option>
                        <option value="MT">Montana</option>
                        <option value="NE">Nebraska</option>
                        <option value="NV">Nevada</option>
                        <option value="NH">New Hampshire</option>
                        <option value="NJ">New Jersey</option>
                        <option value="NM">New Mexico</option>
                        <option value="NY">New York</option>
                        <option value="NC">North Carolina</option>
                        <option value="ND">North Dakota</option>
                        <option value="OH">Ohio</option>
                        <option value="OK">Oklahoma</option>
                        <option value="OR">Oregon</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="RI">Rhode Island</option>
                        <option value="SC">South Carolina</option>
                        <option value="SD">South Dakota</option>
                        <option value="TN">Tennessee</option>
                        <option value="TX">Texas</option>
                        <option value="UT">Utah</option>
                        <option value="VT">Vermont</option>
                        <option value="VA">Virginia</option>
                        <option value="WA">Washington</option>
                        <option value="WV">West Virginia</option>
                        <option value="WI">Wisconsin</option>
                        <option value="WY">Wyoming</option>
                        {/* Add more states as needed */}
                      </Select>

                      <AddressLabel>State / Province</AddressLabel>
                    </div>
                  </AddressRow>
                  <AddressRow>
                    <div style={{ flex: 1 }}>
                      <AddressInput
                        type="text"
                        value={formData.postalCode}
                        onChange={(e) => handleInputChange('postalCode', e.target.value)}
                        maxLength="5"
                        pattern="[0-9]{5}"
                        placeholder="12345"
                        onKeyPress={(e) => {
                          // Only allow numbers
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <AddressLabel>Postal Code</AddressLabel>
                    </div>
                    <div style={{ flex: 1 }}>
                      <Select
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                      >
                        <option value="">- Select</option>
                        {countries.map((country, index) => (
                          <option key={index} value={country}>{country}</option>
                        ))}
                      </Select>
                      <AddressLabel>Country</AddressLabel>
                    </div>
                  </AddressRow>
                </AddressGroup>
              </FormGroup>

              <FormGroup>
                <Label>Signature</Label>
                <SignaturePadContainer>
                  <SignatureCanvas
                    ref={signatureCanvasRef}
                    onMouseDown={startSignatureDrawing}
                    onMouseMove={drawSignature}
                    onMouseUp={stopSignatureDrawing}
                    onMouseLeave={stopSignatureDrawing}
                  />
                  <ClearSignatureButton type="button" onClick={clearSignature}>
                    Clear Signature
                  </ClearSignatureButton>
                </SignaturePadContainer>
              </FormGroup>
            </FormColumn>

            <ButtonGroup>
              <SubmitButton type="submit" disabled={!isFormValid()}>Submit</SubmitButton>
              <ResetButton type="button" onClick={handleReset}>Reset</ResetButton>
            </ButtonGroup>
          </FormGrid>
        </form>
      </FormContainer>
    </Container>
  );
};

export default AddEmployee;
